import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  breadcrumbs?: BreadcrumbItem[];
  schemaData?: Record<string, unknown> | Record<string, unknown>[];
}

export default function SEOMeta({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  breadcrumbs,
  schemaData,
}: SEOMetaProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, propName?: string) => {
      const attr = propName ? 'property' : 'name';
      const selector = propName
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const ogTitleVal = ogTitle || title;
    const ogDescVal = ogDescription || description;
    setMeta('og:title', ogTitleVal, true);
    setMeta('og:description', ogDescVal, true);
    setMeta('og:type', ogType, true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonicalUrl);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitleVal);
    setMeta('twitter:description', ogDescVal);
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType]);

  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    if (schemaData) {
      const schemas = Array.isArray(schemaData) ? schemaData : [schemaData];

      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [schemaData, location.pathname]);

  if (breadcrumbs && breadcrumbs.length > 0) {
    return (
      <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 py-3">
        <ol className="flex items-center flex-wrap gap-1 text-sm text-foreground-500">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return (
              <li key={crumb.path} className="flex items-center gap-1">
                {index > 0 && (
                  <i className="ri-arrow-right-s-line text-xs text-foreground-400"></i>
                )}
                {isLast ? (
                  <span className="text-foreground-700 font-medium">{crumb.name}</span>
                ) : (
                  <a href={crumb.path} className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer">
                    {crumb.name}
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  return null;
}